/**
 * Best-effort fullscreen + orientation lock for the respondent capture flow.
 *
 * Why: on mobile the browser chrome (tabs/"cards") eats screen space, and — more
 * importantly — rotating the phone *mid-recording* produces a half-portrait /
 * half-landscape face clip that breaks emotion parsing and timeline alignment.
 * Entering fullscreen maximises the content area, and locking the orientation to
 * whatever the respondent starts in prevents accidental mid-recording flips.
 *
 * All calls are best-effort and swallow errors:
 * - iOS Safari does NOT support Element.requestFullscreen for non-<video>
 *   elements, so fullscreen is a no-op there (Android Chrome / desktop work).
 * - Screen Orientation lock only works while fullscreen and isn't supported on iOS.
 * Must be invoked from within a user gesture (e.g. a button click).
 */

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}
type FsDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void
  webkitFullscreenElement?: Element | null
}

function isFullscreen(): boolean {
  const d = document as FsDocument
  return !!(d.fullscreenElement || d.webkitFullscreenElement)
}

/** Request fullscreen on the document root. Resolves whether or not it worked. */
export async function enterFullscreen(): Promise<void> {
  if (isFullscreen()) return
  const el = document.documentElement as FsElement
  try {
    if (el.requestFullscreen) {
      await el.requestFullscreen()
    } else if (el.webkitRequestFullscreen) {
      await el.webkitRequestFullscreen()
    }
  } catch {
    /* unsupported (iOS Safari) or blocked — carry on without fullscreen */
  }
}

/** Lock to the orientation the respondent is currently in, so they can't flip
 *  the phone mid-recording (which would corrupt the single face clip). */
export async function lockOrientationToCurrent(): Promise<void> {
  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>
    }
    if (orientation?.lock && orientation.type) {
      // Lock to the broad current axis (portrait/landscape), not the exact
      // primary/secondary, so a small tilt doesn't fight the lock.
      const axis = orientation.type.startsWith('portrait') ? 'portrait' : 'landscape'
      await orientation.lock(axis)
    }
  } catch {
    /* not supported (iOS) or requires fullscreen — ignore */
  }
}

/** Exit fullscreen and release any orientation lock. Safe to call unconditionally. */
export function exitFullscreenAndUnlock(): void {
  try {
    screen.orientation?.unlock?.()
  } catch {
    /* ignore */
  }
  try {
    const d = document as FsDocument
    if (d.fullscreenElement && d.exitFullscreen) {
      void d.exitFullscreen()
    } else if (d.webkitFullscreenElement && d.webkitExitFullscreen) {
      void d.webkitExitFullscreen()
    }
  } catch {
    /* ignore */
  }
}
